import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SearchInput } from "../components/search/SearchInput";
import { Brain, ChevronLeft, ChevronRight, Send, History, AlertTriangle } from "lucide-react";
import { SearchHeader } from "../components/SearchHeader";
import { useRecordsApi } from "../lib/api/records";
import { RagResultCard } from "../components/rag/RagResultCard";
import { RagConfigToggle } from "../components/rag/RagConfigToggle";
import type { DocumentRecord } from "../types/record";
import { appConfig } from "../config/appConfig";
import ReactQuill from "react-quill";
import {
  DocumentPreviewPanel,
  type PreviewDocument,
} from "../components/DocumentPreviewPanel";
import "react-quill/dist/quill.bubble.css";
import type { DeltaStatic } from "quill";
import { ChatMessage as ChatMessageComponent } from "../components/chat/ChatMessage";
import { ConversationHistorySidebar } from "../components/chat/ConversationHistorySidebar";
import { flushSync } from "react-dom";
import { useChat } from "../contexts/ChatContext";
import { useAuth } from "../contexts/AuthContext";
import { useUsage } from "../contexts/UsageContext";
import { useQueryClient } from "@tanstack/react-query";
import { NoSubscriptionScreen } from "../components/chat/NoSubscriptionScreen";
import { UsageLimitBanner } from "../components/chat/UsageLimitBanner";

// Import chat API
import { chatApi, sendChatMessageStreamFetch } from "../lib/api/chat";
import type {
  ChatMessage as ChatMessageType,
  ChatSource,
  IntentType,
  ChatRequest,
  ChatStreamChunk,
} from "../types/chat";

// Import conversation service for loading message history
import { conversationService, ConversationAccessError } from "../services/conversationService";
import type { Message as ConversationMessage } from "../types/conversation";

import intelligentiaLogo from "../images/intelligentiahumana.jpg";

// Define the RagSource interface for backward compatibility
interface RagSource {
  id: string;
  title?: string;
  score: number;
  content?: string;
  formatted_content?: DeltaStatic;
}

// Define the Message interface for chat
interface Message {
  role: "user" | "assistant";
  content: string;
  sources?: RagSource[];
  timestamp: number;
  conversationId?: string;
  messageIndex?: number;
  intent?: IntentType;
  classification?: {
    intent: IntentType;
    confidence: number;
    reasoning?: string;
  };
  isStreaming?: boolean;
  isHistorical?: boolean; // Flag to indicate if message is from chat history (no typewriter effect)
  isConversationNotice?: boolean; // Flag to indicate this is a conversation continuation notice
}

export function ChatPage() {
  const recordsApi = useRecordsApi(); // Keep for document loading
  const queryClient = useQueryClient();
  const {
    conversationId,
    setConversationId,
    createNewConversation,
    clearConversation,
  } = useChat();
  const { clerkUser } = useAuth();
  const { hasSubscription, usage, updateRemainingFromResponse } = useUsage();
  const { conversationId: urlConversationId } = useParams<{
    conversationId: string;
  }>();
  const navigate = useNavigate();

  // Check if conversation history feature is enabled
  const showConversationHistory = appConfig.chat.enableConversationHistory;
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  // Keep sessionId for backwards compatibility but prefer conversationId
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
  const [thinkingTime, setThinkingTime] = useState<number>(0);
  const thinkingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<PreviewDocument | null>(
    null
  );
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const quillRef = useRef<ReactQuill>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState("");

  // Load chat history for a conversation
  const loadChatHistory = async (conversationId: string) => {
    try {
      setIsLoading(true);
      console.log("Loading chat history for conversation:", conversationId);

      if (!clerkUser) {
        throw new Error("User not authenticated");
      }

      // Use the conversation service to get the actual stored messages
      const conversationDetail =
        await conversationService.getConversationDetail(conversationId);

      // Convert ConversationMessage[] to Message[] for the chat UI
      const convertedMessages: Message[] = conversationDetail.messages.map(
        (msg: ConversationMessage, index: number) => ({
          role: msg.sender as "user" | "assistant",
          content: msg.content,
          timestamp: new Date(msg.created_at).getTime(),
          conversationId: conversationId,
          messageIndex: msg.message_index || index, // Use stored message_index or fallback to array index
          sources: [], // Chat UI doesn't show sources from history yet
          isHistorical: true, // Mark historical messages to disable typewriter effect
        })
      );

      // Add conversation continuation notice ONLY if we're loading an existing conversation with actual messages
      // This should NOT appear for new conversations that happen to have a conversation ID but no stored messages
      if (convertedMessages.length > 0) {
        const continuationNotice: Message = {
          role: "assistant", // Use assistant role for styling consistency
          content: `Reprise de la conversation du ${new Date(
            convertedMessages[0]?.timestamp || Date.now()
          ).toLocaleDateString()} • ${convertedMessages.length} message${
            convertedMessages.length !== 1 ? "s" : ""
          } chargé${convertedMessages.length !== 1 ? "s" : ""}`,
          timestamp: Date.now(),
          isHistorical: true,
          isConversationNotice: true, // Special flag for styling
        };
        convertedMessages.push(continuationNotice);
      }

      // Only overwrite messages if there are no current messages OR
      // if the loaded messages are more recent than what we have
      setMessages((currentMessages) => {
        // If we have current messages and the history is empty, keep current messages
        // This prevents overwriting messages that were just added for a new conversation
        if (currentMessages.length > 0 && convertedMessages.length === 0) {
          console.log(
            "Preserving current messages - history is empty for new conversation"
          );
          return currentMessages;
        }

        // If history is empty (no stored messages), don't add anything including continuation notice
        if (convertedMessages.length === 0) {
          console.log("No messages found in conversation history");
          return [];
        }

        // If both have messages, use the history (existing conversation)
        console.log(
          `Loaded ${convertedMessages.length} messages from conversation history`
        );
        return convertedMessages;
      });
    } catch (error: any) {
      console.error("Failed to load conversation history:", error);
      console.log("Error type:", error?.constructor?.name);
      console.log("Error status:", error?.status);

      // Feature: 001-user-role-management (T044) - Handle 401/403 when loading conversation
      if (error instanceof ConversationAccessError && (error.status === 401 || error.status === 403)) {
        console.log("Access denied detected, setting accessDenied to true");
        const errorMessage = error.detail || "You don't have permission to access this conversation";
        setError(`Access Denied: ${errorMessage}`);
        setAccessDenied(true);

        // Don't set messages - let the empty state show the access denied message
        setMessages([]);
      } else {
        setError("Failed to load conversation history");
        // Don't fail completely, just preserve current messages if any
        setMessages((currentMessages) => {
          if (currentMessages.length > 0) {
            console.log("Preserving current messages due to history load error");
            return currentMessages;
          }
          return [];
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Sync conversation ID from URL and load history if available
  useEffect(() => {
    if (urlConversationId) {
      // Set conversation ID from URL
      setConversationId(urlConversationId);

      // Load chat history for this conversation
      loadChatHistory(urlConversationId);
    } else {
      // No conversation ID in URL, clear current conversation
      setConversationId(null);
      setMessages([]);
    }
  }, [urlConversationId, setConversationId]);

  const clearSession = () => {
    // Navigate to /chat to start a new conversation
    navigate("/chat");

    // Clear messages and conversation state
    clearConversation();
    setMessages([]);
    setAccessDenied(false);
    setError(null);

    // Generate new session ID for backwards compatibility
    setSessionId(crypto.randomUUID());

    // Refresh conversation history sidebar
    queryClient.invalidateQueries({ queryKey: ['my-conversations'] });
  };

  // Scroll only the messages container — never the page — so the fixed
  // header stays visible while assistant tokens stream in.
  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Convert ChatSource to RagSource for UI compatibility
  const convertChatSourceToRagSource = (source: ChatSource): RagSource => ({
    id: source.id,
    title: source.title,
    score: source.score || 0,
    content: source.content,
  });

  // Prepare chat history for API request
  const prepareChatHistory = (): ChatMessageType[] => {
    return messages.slice(-10).map((msg) => ({
      role: msg.role as "user" | "assistant",
      content: msg.content,
      timestamp: new Date(msg.timestamp).toISOString(),
    }));
  };

  // Check if conversation has reached message limit
  const checkMessageLimit = (): boolean => {
    // Count ALL messages in the conversation (historical + new), excluding only conversation notices
    const actualMessages = messages.filter((msg) => !msg.isConversationNotice);

    // Check if we've reached 10 total messages (user + assistant)
    return actualMessages.length >= 10;
  };

  // Handle search with new intent-based chat API
  const handleSearch = async (newQuery: string) => {
    if (!newQuery.trim()) return;

    // Check message limit before proceeding
    if (checkMessageLimit()) {
      // Show error and force new conversation
      setError(
        "Cette conversation a atteint la limite de 10 messages. Démarrage d'une nouvelle conversation pour de meilleures réponses IA avec un contexte limité."
      );

      // Wait a moment for user to see the message, then start new conversation
      setTimeout(() => {
        clearSession();
      }, 3000);
      return;
    }

    setError(null);
    setInputValue("");

    // Add user message
    const userMessage: Message = {
      role: "user",
      content: newQuery,
      timestamp: Date.now(),
      conversationId: conversationId || undefined,
      messageIndex: messages.length, // Index for this new message
      isHistorical: false, // New user messages are not historical
    };
    console.log("Adding user message to chat:", userMessage);
    setMessages((prev) => {
      console.log("Current messages before adding user message:", prev.length);
      return [...prev, userMessage];
    });

    // Cancel any ongoing generation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsLoading(true);
    startThinkingTimer();

    try {
      if (appConfig.rag.useStreaming) {
        await handleStreamingChat(newQuery);
      } else {
        await handleSingleCallChat(newQuery);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Chat error:", error);
        setError(
          error instanceof Error
            ? error.message
            : "An error occurred while generating the response"
        );
      }
    } finally {
      setIsLoading(false);
      stopThinkingTimer();

      // Refresh conversation history sidebar after message is sent
      queryClient.invalidateQueries({ queryKey: ['my-conversations'] });
    }
  };

  // Handle single call chat (non-streaming)
  const handleSingleCallChat = async (query: string) => {
    try {
      let currentConversationId = conversationId;

      // If no conversation ID, create a new conversation
      if (!currentConversationId) {
        console.log("Creating new conversation for single call chat");
        currentConversationId = await createNewConversation();
        console.log("Navigating to new conversation:", currentConversationId);
        // Navigate to the new conversation URL
        navigate(`/chat/${currentConversationId}`);
      }

      if (!clerkUser) {
        throw new Error("User not authenticated");
      }

      const chatRequest: ChatRequest = {
        message: query,
        conversation_id: currentConversationId,
        session_id: sessionId, // Keep for backwards compatibility
      };

      const response = await chatApi.sendMessage(chatRequest);

      if (response.usage_remaining !== undefined && response.usage_remaining !== null) {
        updateRemainingFromResponse(response.usage_remaining);
      }

      // Add assistant message
      const assistantMessage: Message = {
        role: "assistant",
        content: response.response,
        sources: [], // Chat endpoint doesn't return sources
        timestamp: Date.now(),
        isHistorical: false, // New assistant messages are not historical
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      handleChatError(error);
    }
  };

  // Handle streaming chat with chat API
  const handleStreamingChat = async (query: string) => {
    try {
      let responseText = "";
      let currentConversationId = conversationId;

      // If no conversation ID, create a new conversation
      if (!currentConversationId) {
        console.log("Creating new conversation for streaming chat");
        currentConversationId = await createNewConversation();
        console.log("Navigating to new conversation:", currentConversationId);
        // Navigate to the new conversation URL
        navigate(`/chat/${currentConversationId}`);
      }

      if (!clerkUser) {
        throw new Error("User not authenticated");
      }

      const chatRequest: ChatRequest = {
        message: query,
        conversation_id: currentConversationId,
        session_id: sessionId, // Keep for backwards compatibility
        use_streaming: true,
      };

      // Add initial assistant message with streaming flag
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "",
          timestamp: Date.now(),
          conversationId: currentConversationId,
          messageIndex: prev.length, // Index for this new assistant message
          isStreaming: true,
          isHistorical: false, // New streaming messages are not historical
        },
      ]);

      // Stream response using the chat API
      for await (const chunk of sendChatMessageStreamFetch(chatRequest)) {
        console.log("Received chunk:", chunk);

        if (!chunk.done) {
          // Add chunk content if present (including empty strings for formatting)
          if (chunk.chunk !== undefined) {
            responseText += chunk.chunk;
          }

          // Batch updates for better performance - update UI less frequently
          // This reduces the number of re-renders while maintaining responsiveness
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === "assistant") {
              lastMessage.content = responseText;
              lastMessage.isStreaming = true;
            }
            return newMessages;
          });
        }

        if (chunk.done) {
          // Check if this is an access denied error
          if (chunk.chunk && chunk.chunk.includes("Access Denied:")) {
            setAccessDenied(true);
            setError(chunk.chunk);
            // Update the assistant message with the error
            setMessages((prev) => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              if (lastMessage && lastMessage.role === "assistant") {
                lastMessage.content = chunk.chunk;
                lastMessage.isStreaming = false;
              }
              return newMessages;
            });
            break;
          }

          // Final chunk with metadata
          if (chunk.full_response) {
            responseText = chunk.full_response;
          }

          if (chunk.usage_remaining !== undefined && chunk.usage_remaining !== null) {
            updateRemainingFromResponse(chunk.usage_remaining);
          }

          // Final update with complete data and mark streaming as complete
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === "assistant") {
              lastMessage.content = responseText;
              (lastMessage.sources = []), // Chat endpoint doesn't return sources
                (lastMessage.isStreaming = false);
            }
            return newMessages;
          });
          break;
        }
      }
    } catch (error) {
      handleChatError(error);
    }
  };

  // Handle chat errors
  // Feature: 001-user-role-management (T043-T044) - Handle 403 authorization errors
  const handleChatError = (error: any) => {
    console.error("Chat error:", error);

    // Check for 429 Usage Limit Reached
    if (error?.status === 429 || error?.response?.status === 429) {
      // Update usage context to reflect limit reached
      updateRemainingFromResponse(0);

      // Remove the streaming assistant message if present
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && last?.isStreaming) {
          return prev.slice(0, -1);
        }
        return prev;
      });

      setError(null); // Clear generic error, UsageLimitBanner will show
      return;
    }

    // Check for 403 Forbidden (authorization error)
    if (error?.response?.status === 403) {
      const errorMessage =
        error?.response?.data?.detail ||
        "You don't have permission to access this conversation";

      setAccessDenied(true);

      // Add specific 403 error message to chat
      const errorMessageObj: Message = {
        role: "assistant",
        content: `Access Denied: ${errorMessage}. This conversation may belong to another user.`,
        timestamp: Date.now(),
        isHistorical: false,
      };
      setMessages((prev) => [...prev, errorMessageObj]);
      setError(errorMessage);
      return;
    }

    // Handle other errors
    const errorMessage =
      error?.response?.data?.detail ||
      error?.message ||
      "An error occurred while processing your request";

    // Add error message to chat
    const errorMessageObj: Message = {
      role: "assistant",
      content: `Sorry, I encountered an error: ${errorMessage}`,
      timestamp: Date.now(),
      isHistorical: false, // Error messages are new messages, not historical
    };
    setMessages((prev) => [...prev, errorMessageObj]);
  };

  const startThinkingTimer = () => {
    setThinkingTime(0);
    thinkingTimerRef.current = setInterval(() => {
      setThinkingTime((prev) => prev + 0.1);
    }, 100);
  };

  const stopThinkingTimer = () => {
    if (thinkingTimerRef.current) {
      clearInterval(thinkingTimerRef.current);
      thinkingTimerRef.current = null;
    }
  };

  const formatThinkingTime = (
    time: number,
    showMilliseconds: boolean = false
  ): string => {
    if (time < 1) {
      return showMilliseconds ? `${(time * 1000).toFixed(0)}ms` : "<1";
    } else if (time < 60) {
      return time.toFixed(1);
    } else {
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}m ${seconds}s`;
    }
  };

  // Document handling functions remain the same
  const handleDocumentClick = async (document: PreviewDocument) => {
    setSelectedDocument({ ...document, content: "Loading content..." });
    setIsDocumentLoading(true);

    try {
      const response = await recordsApi.getDocumentContent(document.id);
      setSelectedDocument((prev) =>
        prev
          ? {
              ...prev,
              content: response.content,
              formatted_content: response.formatted_content,
            }
          : null
      );
    } catch (error) {
      console.error("Error loading document:", error);
      setSelectedDocument((prev) =>
        prev
          ? {
              ...prev,
              content: "Error loading content. Please try again.",
            }
          : null
      );
    } finally {
      setIsDocumentLoading(false);
    }
  };

  const navigateToPrevDocument = async () => {
    if (!selectedDocument || messages.length === 0) return;

    const currentMessage = messages
      .slice()
      .reverse()
      .find((msg) => msg.role === "assistant" && msg.sources?.length);

    if (currentMessage?.sources) {
      const currentIndex = currentMessage.sources.findIndex(
        (source) => source.id === selectedDocument.id
      );

      if (currentIndex > 0) {
        const prevDocument = currentMessage.sources[currentIndex - 1];
        await handleDocumentClick(prevDocument);
      }
    }
  };

  const navigateToNextDocument = async () => {
    if (!selectedDocument || messages.length === 0) return;

    const currentMessage = messages
      .slice()
      .reverse()
      .find((msg) => msg.role === "assistant" && msg.sources?.length);

    if (currentMessage?.sources) {
      const currentIndex = currentMessage.sources.findIndex(
        (source) => source.id === selectedDocument.id
      );

      if (currentIndex < currentMessage.sources.length - 1) {
        const nextDocument = currentMessage.sources[currentIndex + 1];
        await handleDocumentClick(nextDocument);
      }
    }
  };

  const closeDocumentView = () => {
    setSelectedDocument(null);
  };

  // Handle keyboard navigation for document preview
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedDocument) return;

      if (e.key === "ArrowLeft") {
        navigateToPrevDocument();
      } else if (e.key === "ArrowRight") {
        navigateToNextDocument();
      } else if (e.key === "Escape") {
        closeDocumentView();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedDocument, messages]);

  return (
    <div className="relative flex h-screen flex-col bg-brand-surface overflow-hidden">
      {/* Ambient aura — pinned to viewport so it doesn't grow with the page */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-0 overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full opacity-30 blur-3xl bg-gradient-to-br from-brand-accent to-brand-teal" />
        <div className="absolute bottom-[-25%] right-[-10%] w-[700px] h-[700px] rounded-full opacity-20 blur-3xl bg-gradient-to-tr from-brand-teal to-brand-accent" />
      </div>

      <SearchHeader />

      {/* Conversation History Sidebar */}
      {showConversationHistory && (
        <ConversationHistorySidebar
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          onNewConversation={clearSession}
        />
      )}

      <div className={`relative z-10 mx-auto flex w-full max-w-7xl flex-1 min-h-0 flex-col px-4 pt-24 pb-4 transition-all duration-300 ${
        isHistoryOpen ? 'ml-80' : ''
      }`}>
        <div className={`flex w-full flex-1 min-h-0 flex-col items-center text-center ${
          messages.length === 0 && !conversationId ? 'justify-center' : ''
        }`}>
          <img
            src={intelligentiaLogo}
            alt="Intelligentia Logo"
            className="w-80 object-contain shrink-0"
          />

          <p className="mb-8 text-lg text-brand-text shrink-0">
            Accéder à{" "}
            <span className="font-semibold bg-gradient-to-r from-brand-accent to-brand-teal bg-clip-text text-transparent">
              l'intelligence humaine
            </span>{" "}
            propulsée par l'IA
          </p>

          <div className={`relative flex w-full justify-center ${
            messages.length > 0 || conversationId ? 'flex-1 min-h-0' : ''
          }`}>
            <div className={`flex w-full max-w-6xl flex-col ${
              messages.length > 0 || conversationId ? 'min-h-0' : ''
            }`}>
              {/* No Subscription Banner */}
              {!hasSubscription && (
                <NoSubscriptionScreen />
              )}

              {/* Access Denied Banner */}
              {hasSubscription && accessDenied && (
                <div className="mb-4 p-4 bg-brand-error/10 border border-brand-error/50 rounded-lg">
                  <div className="flex items-center gap-3 text-brand-error">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">Access Denied</p>
                      <p className="text-sm text-brand-error/80 mt-1">{error || "You don't have permission to access this conversation."}</p>
                    </div>
                  </div>
                </div>
              )}

              {hasSubscription && <>
              {/* Chat Messages — only render once a conversation exists */}
              {(messages.length > 0 || conversationId) && (
              <div ref={messagesContainerRef} className="mb-4 flex-1 min-h-0 overflow-y-auto p-4 bg-transparent [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-brand-border [&::-webkit-scrollbar-thumb]:max-h-20 hover:[&::-webkit-scrollbar-thumb]:bg-brand-muted/30">
                {/* Show loading state for conversation history */}
                {isLoading && messages.length === 0 && conversationId && (
                  <div className="flex flex-col items-center justify-center py-8 text-brand-muted">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-border mb-3"></div>
                    <p className="text-sm">Loading conversation history...</p>
                  </div>
                )}

                {/* Show empty state for existing conversation with no messages */}
                {conversationId && messages.length === 0 && !isLoading && (
                  <div className="mb-4 p-4 bg-brand-bg rounded-lg border border-brand-border text-center">
                    <div className="text-brand-muted text-sm">
                      <Brain className="w-5 h-5 mx-auto mb-2 opacity-50" />
                      {accessDenied ? (
                        <>
                          <p className="text-brand-error font-medium">Access Denied</p>
                          <p className="text-xs mt-1 opacity-75">
                            You don't have permission to access this conversation.
                          </p>
                          <p className="text-xs mt-1 opacity-75">
                            This conversation belongs to another user.
                          </p>
                        </>
                      ) : (
                        <>
                          <p>This conversation exists but has no messages yet.</p>
                          <p className="text-xs mt-1 opacity-75">
                            Start chatting to continue the conversation.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Render messages */}
                {messages.map((message, index) => (
                  <div key={index} className="mb-4">
                    <ChatMessageComponent
                      message={message}
                      onDocumentClick={handleDocumentClick}
                    />
                    {/* Show intent classification for debugging */}
                    {message.classification &&
                      process.env.NODE_ENV === "development" && (
                        <div className="mt-2 text-xs text-brand-muted bg-brand-bg rounded p-2">
                          Intent: {message.classification.intent}
                          (confidence:{" "}
                          {(message.classification.confidence * 100).toFixed(1)}
                          %)
                          {message.classification.reasoning && (
                            <div>
                              Reasoning: {message.classification.reasoning}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              )}

              {/* Usage Limit Banner */}
              <UsageLimitBanner />

              {/* Chat Input */}
              <div className="relative shrink-0">
                <SearchInput
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onSearch={(value) => {
                    if (value.trim()) {
                      handleSearch(value);
                    }
                  }}
                  placeholder={accessDenied ? "Access denied - Cannot send messages" : "Posez votre question ou discutez avec moi..."}
                  disabled={isLoading || checkMessageLimit() || accessDenied || usage?.remaining === 0}
                  actions={
                    <>
                      <button
                        type="button"
                        onClick={clearSession}
                        className="px-3 py-1.5 text-sm text-brand-muted border border-brand-border rounded-full transition-colors hover:bg-brand-accent hover:text-white disabled:opacity-50"
                        disabled={isLoading}
                      >
                        ✕ Réinitialiser
                      </button>
                      {showConversationHistory && (
                        <button
                          type="button"
                          onClick={() => setIsHistoryOpen((prev) => !prev)}
                          className="px-3 py-1.5 text-sm text-brand-muted border border-brand-border rounded-full transition-colors hover:bg-brand-accent hover:text-white flex items-center gap-1.5 disabled:opacity-50"
                          disabled={isLoading}
                        >
                          <History className="w-3.5 h-3.5" />
                          Historique
                        </button>
                      )}
                      <div className="flex-1" />
                      <RagConfigToggle />
                    </>
                  }
                />

                {/* AI Disclaimer */}
                <p className="mt-4 text-sm text-brand-muted text-center">
                  Le contenu généré par l'intelligence artificielle peut être inexact, incomplet ou ne pas refléter exactement les réalités juridiques, organisationnelles ou opérationnelles de votre milieu.
                </p>

                {/* Usage warning at 75%+ */}
                {usage && usage.remaining > 0 && usage.remaining <= Math.ceil(usage.limit * 0.25) && (
                  <div className="mt-2 flex items-center justify-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-brand-teal" />
                    <p className="text-sm text-brand-teal">
                      {usage.remaining} {usage.metering_mode === "chats" ? "conversation" : "message"}{usage.remaining > 1 ? "s" : ""} restant{usage.remaining > 1 ? "s" : ""} sur votre forfait — réinitialisation le {new Date(usage.period_end).toLocaleDateString("fr-CA", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                  </div>
                )}

                {/* Message limit indicator */}
                {(() => {
                  const actualMessages = messages.filter(
                    (msg) => !msg.isConversationNotice
                  );
                  const remainingMessages = 10 - actualMessages.length;

                  // Show indicator when approaching or exceeding the limit (8+ messages or already over 10)
                  if (actualMessages.length >= 8 || remainingMessages <= 0) {
                    return (
                      <div className="mt-2 text-center">
                        <div className="text-sm text-brand-muted">
                          {remainingMessages <= 0
                            ? "Limite de conversation atteinte - Nouvelle conversation requise pour assurer de meilleures réponses avec un contexte limité"
                            : `${remainingMessages} message${
                                remainingMessages !== 1 ? "s" : ""
                              } restant${
                                remainingMessages !== 1 ? "s" : ""
                              } avant limite`}
                        </div>
                        {remainingMessages <= 2 && remainingMessages > 0 && (
                          <div className="text-xs text-brand-muted mt-1">
                            Les nouvelles conversations offrent de meilleures
                            réponses avec un contexte limité
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {isLoading && (
                <div className="mt-4 text-brand-text">
                  <div className="flex items-center gap-4">
                    <div className="animate-pulse">
                      Réflexion - {formatThinkingTime(thinkingTime)} secondes
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-4 rounded-xl bg-brand-error/10 p-4 text-brand-text">
                  {error}
                </div>
              )}

              </>}

              {/* Document Preview - shared with the agent chat surface */}
              <DocumentPreviewPanel
                document={selectedDocument}
                onClose={closeDocumentView}
                onRetry={handleDocumentClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
